import { useReducer, useCallback } from "react";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";

type CalculadoraState = {
  selectedMaquininha: string;
  taxaAtual: number;
  valorAntecipar: number;
  numeroParcelas: number;
  economia: number;
  custoDrCash: number;
  custoConcorrente: number;
  isCalculating: boolean;
  errorMessage: string | null;
  animateResult: boolean;
  lastUpdated: 'taxa' | 'valor' | 'maquininha' | 'parcelas' | null;
};

type CalculadoraAction =
  | { type: "SET_MAQUININHA"; payload: string }
  | { type: "SET_TAXA"; payload: number }
  | { type: "SET_VALOR"; payload: number }
  | { type: "SET_PARCELAS"; payload: number }
  | { type: "CALCULATE_START" }
  | { type: "CALCULATE_SUCCESS"; payload: { economia: number; custoDrCash: number; custoConcorrente: number } }
  | { type: "CALCULATE_ERROR"; payload: string }
  | { type: "RESET_ANIMATION" };

const valorSchema = z.number().min(250, "O valor mínimo é R$ 250,00").max(1000000, "O valor máximo é R$ 1.000.000,00");
const taxaSchema = z.number().min(0.1, "A taxa mínima é 0,1%").max(15, "A taxa máxima é 15%");

const initialState: CalculadoraState = {
  selectedMaquininha: "",
  taxaAtual: 3.0,
  valorAntecipar: 10000,
  numeroParcelas: 12,
  economia: 0,
  custoDrCash: 0,
  custoConcorrente: 0,
  isCalculating: false,
  errorMessage: null,
  animateResult: false,
  lastUpdated: null,
};

const DR_CASH_RATES = [
  1.90, // mês 1
  1.90, // mês 2
  1.90, // mês 3
  1.90, // mês 4
  1.90, // mês 5
  1.90, // mês 6
  1.90, // mês 7
  1.90, // mês 8
  1.90, // mês 9
  1.90, // mês 10
  1.90, // mês 11
  1.90  // mês 12
];

function calculadoraReducer(state: CalculadoraState, action: CalculadoraAction): CalculadoraState {
  switch (action.type) {
    case "SET_MAQUININHA":
      return { ...state, selectedMaquininha: action.payload, lastUpdated: 'maquininha', errorMessage: null };
    case "SET_TAXA":
      return { ...state, taxaAtual: action.payload, lastUpdated: 'taxa', errorMessage: null };
    case "SET_VALOR":
      return { ...state, valorAntecipar: action.payload, lastUpdated: 'valor', errorMessage: null };
    case "SET_PARCELAS":
      return { ...state, numeroParcelas: action.payload, lastUpdated: 'parcelas', errorMessage: null };
    case "CALCULATE_START":
      return { ...state, isCalculating: true, errorMessage: null };
    case "CALCULATE_SUCCESS":
      return {
        ...state,
        economia: action.payload.economia,
        custoDrCash: action.payload.custoDrCash,
        custoConcorrente: action.payload.custoConcorrente,
        isCalculating: false,
        animateResult: true,
      };
    case "CALCULATE_ERROR":
      return { ...state, errorMessage: action.payload, isCalculating: false };
    case "RESET_ANIMATION":
      return { ...state, animateResult: false };
    default:
      return state;
  }
}

export const useCalculadoraAntecipacao = () => {
  const [state, dispatch] = useReducer(calculadoraReducer, initialState);
  const { toast } = useToast();

  const formatToBRL = useCallback((value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }, []);

  const currencyToNumber = useCallback((value: string): number => {
    return Number(value.replace(/[^\d,-]/g, '').replace(',', '.')) || 0;
  }, []);

  const calcularAntecipaçãoMensal = useCallback((valorParcela: number, taxa: number): number => {
    return valorParcela * (taxa / 100);
  }, []);

  const calcularCustoTotalAntecipação = useCallback((valor: number, taxas: number[], totalMeses: number): number => {
    const valorParcela = valor / totalMeses;
    let custoTotal = 0;

    for (let mes = 0; mes < totalMeses; mes++) {
      const taxa = taxas[mes];
      const custoMes = calcularAntecipaçãoMensal(valorParcela, taxa);
      const mesesRestantes = totalMeses - mes;
      custoTotal += custoMes * mesesRestantes;
    }

    return custoTotal;
  }, [calcularAntecipaçãoMensal]);

  const resetAnimation = useCallback(() => {
    setTimeout(() => {
      dispatch({ type: "RESET_ANIMATION" });
    }, 1000);
  }, []);

  const calcularEconomia = useCallback(() => {
    try {
      dispatch({ type: "CALCULATE_START" });
      
      try {
        valorSchema.parse(state.valorAntecipar);
        taxaSchema.parse(state.taxaAtual);
      } catch (error) {
        if (error instanceof z.ZodError) {
          const errorMsg = error.issues[0]?.message || "Valor ou taxa inválidos";
          dispatch({ type: "CALCULATE_ERROR", payload: errorMsg });
          toast({
            title: "Erro de validação",
            description: errorMsg,
            variant: "destructive",
          });
          return;
        }
      }

      const TAXAS_CONCORRENTE = Array(state.numeroParcelas).fill(state.taxaAtual);
      
      setTimeout(() => {
        const taxasDrCash = DR_CASH_RATES.slice(0, state.numeroParcelas);
        const taxasConcorrente = TAXAS_CONCORRENTE.slice(0, state.numeroParcelas);
        
        const valorTotalDrCash = calcularCustoTotalAntecipação(
          state.valorAntecipar, 
          taxasDrCash, 
          state.numeroParcelas
        );
        
        const valorTotalConcorrente = calcularCustoTotalAntecipação(
          state.valorAntecipar, 
          taxasConcorrente, 
          state.numeroParcelas
        );
        
        const economiaTotal = Math.max(0, valorTotalConcorrente - valorTotalDrCash);
        
        dispatch({
          type: "CALCULATE_SUCCESS",
          payload: {
            economia: economiaTotal,
            custoDrCash: valorTotalDrCash,
            custoConcorrente: valorTotalConcorrente
          }
        });
        
        toast({
          title: "Cálculo realizado com sucesso",
          description: `Sua economia será de ${formatToBRL(economiaTotal)}`,
          variant: "default",
        });
        
        resetAnimation();
        
      }, 800);

    } catch (error) {
      const errorMessage = "Erro ao calcular economia. Verifique os valores informados.";
      dispatch({ 
        type: "CALCULATE_ERROR", 
        payload: errorMessage 
      });
      toast({
        title: "Erro no cálculo",
        description: errorMessage,
        variant: "destructive",
      });
    }
  }, [state.valorAntecipar, state.taxaAtual, state.numeroParcelas, state.selectedMaquininha, calcularCustoTotalAntecipação, formatToBRL, toast, resetAnimation]);

  const setSelectedMaquininha = useCallback((value: string) => {
    dispatch({ type: "SET_MAQUININHA", payload: value });
  }, []);

  const setTaxaAtual = useCallback((value: number) => {
    dispatch({ type: "SET_TAXA", payload: value });
  }, []);

  const setValorAntecipar = useCallback((value: number) => {
    dispatch({ type: "SET_VALOR", payload: value });
  }, []);

  const setNumeroParcelas = useCallback((value: number) => {
    dispatch({ type: "SET_PARCELAS", payload: value });
  }, []);

  return {
    ...state,
    setSelectedMaquininha,
    setTaxaAtual,
    setValorAntecipar,
    setNumeroParcelas,
    calcularEconomia,
    formatToBRL,
    currencyToNumber,
    isCalculating: state.isCalculating,
    errorMessage: state.errorMessage,
    animateResult: state.animateResult,
    lastUpdated: state.lastUpdated
  };
};
